import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { mapSegmentName } from '@app/pages/checkout-app/checkout-layout/main/components/ab-testing/react/features/list-ab-tests/AbTestCard/utils';
import { RevenueUpliftDataPoint } from '../../impact-dashboard-metric.models';
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexYAxis,
  ApexMarkers,
  ApexTooltip,
  ApexFill,
  ApexGrid,
} from 'ng-apexcharts';

// Chart color constants
const CHART_PRIMARY_COLOR = '#2D9B87';
const CHART_AXIS_COLOR = '#6B7280';
const CHART_GRID_COLOR = '#E5E7EB';

export type RevenueUpliftChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  markers: ApexMarkers;
  tooltip: ApexTooltip;
  fill: ApexFill;
  grid: ApexGrid;
};

@Component({
  selector: 'revenue-uplift-progress-chart',
  standalone: false,
  templateUrl: './revenue-uplift-progress-chart.component.html',
  styleUrl: './revenue-uplift-progress-chart.component.scss',
})
export class RevenueUpliftProgressChartComponent implements OnChanges {
  @ViewChild('chart') chart: ChartComponent;

  @Input() dataPoints: RevenueUpliftDataPoint[] = [];
  @Input() currencySymbol = '$';
  @Input() loading = false;
  @Input() error = false;

  chartOptions: RevenueUpliftChartOptions;

  constructor() {
    this.initializeChartOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataPoints']?.currentValue || changes['currencySymbol']?.currentValue) {
      this.updateChartData();
    }
  }

  private initializeChartOptions(): void {
    this.chartOptions = {
      series: [
        {
          name: 'Cumulative Revenue Uplift',
          data: [],
        },
      ],
      chart: {
        type: 'area',
        height: 350,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        fontFamily: 'Inter, sans-serif',
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: [CHART_PRIMARY_COLOR],
      },
      fill: {
        type: 'gradient',
        gradient: {
          type: 'vertical',
          shadeIntensity: 1,
          gradientToColors: [CHART_PRIMARY_COLOR],
          opacityFrom: 0.4,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      markers: {
        size: 6,
        colors: ['#fff'],
        strokeColors: CHART_PRIMARY_COLOR,
        strokeWidth: 2,
        hover: {
          size: 8,
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM dd',
          style: {
            colors: CHART_AXIS_COLOR,
            fontSize: '12px',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          formatter: (value: number) => this.formatChartCurrency(value),
          style: {
            colors: CHART_AXIS_COLOR,
            fontSize: '12px',
          },
        },
      },
      tooltip: {
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
          const dataPoint = this.dataPoints[dataPointIndex];
          if (!dataPoint) return '';

          const formattedDate = this.formatDate(dataPoint.implementedAt);
          const formattedSegment = mapSegmentName(dataPoint.segmentImplemented);
          const segment = formattedSegment !== '-' ? formattedSegment : 'All Users';

          return `
            <div style="background-color: #1f2937; border-radius: 8px; padding: 12px 16px; color: white; font-family: 'Inter', sans-serif; min-width: 200px;">
              <div style="font-size: 14px; font-weight: 600; margin-bottom: 4px;">${dataPoint.testName} (${formattedDate})</div>
              <div style="font-size: 12px; font-weight: 400; opacity: 0.8; margin-bottom: 8px;">Implemented Segment: ${segment}</div>
              <div style="font-size: 14px; font-weight: 700; color: ${CHART_PRIMARY_COLOR};">Total Revenue Uplift: ${this.formatCurrency(dataPoint.cumulativeUplift)}</div>
            </div>
          `;
        },
      },
      grid: {
        borderColor: CHART_GRID_COLOR,
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
    };
  }

  private updateChartData(): void {
    if (!this.dataPoints || this.dataPoints.length === 0) {
      return;
    }

    const chartData = this.dataPoints.map((point) => ({
      x: new Date(point.implementedAt).getTime(),
      y: point.cumulativeUplift,
    }));

    this.chartOptions.series = [
      {
        name: 'Cumulative Revenue Uplift',
        data: chartData,
      },
    ];

    this.updateYAxisFormatter();
    this.updateYAxisTickAmount();
  }

  private updateYAxisTickAmount(): void {
    const maxValue = Math.max(...this.dataPoints.map((p) => p.cumulativeUplift));

    if (maxValue >= 1000000) {
      const tickAmount = Math.ceil(maxValue / 500000);
      this.chartOptions.yaxis = {
        ...this.chartOptions.yaxis,
        tickAmount: tickAmount,
        max: tickAmount * 500000,
        min: 0,
      };
    }
  }

  private updateYAxisFormatter(): void {
    this.chartOptions.yaxis = {
      ...this.chartOptions.yaxis,
      labels: {
        ...this.chartOptions.yaxis.labels,
        formatter: (value: number) => this.formatChartCurrency(value),
      },
    };
  }

  private formatChartCurrency(value: number): string {
    if (value >= 1000000) {
      return `${this.currencySymbol}${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${this.currencySymbol}${(value / 1000).toFixed(0)}K`;
    }
    return `${this.currencySymbol}${value.toFixed(0)}`;
  }

  private formatCurrency(value: number | null | undefined): string {
    if (value == null) return `${this.currencySymbol}0`;
    return `${this.currencySymbol}${Math.round(value).toLocaleString('en-US')}`;
  }

  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}
